import { FolderPlusIcon, SplitIcon } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/core/alert';
import { Button } from '@/components/core/button';

import { WorkflowEditorState } from '../use-workflow-editor-state';
import { addGroup, addLeaf, setName } from './condition-draft';
import { ConditionGroup } from './condition-group';
import { ConditionHeader } from './condition-header';

/**
 * Side panel for editing a rule's condition. Purely controlled off
 * `editor.rules[ruleIndex]`, staging every edit through `updateRule`; a
 * `tooDeep` rule shows a contact-support alert instead.
 */
export function ConditionPanel({
  editor,
  ruleIndex,
}: {
  editor: WorkflowEditorState;
  ruleIndex: number;
}) {
  const { scopeType } = editor.context;
  const rule = editor.rules[ruleIndex];
  const { tooDeep } = rule;

  const incompleteLeafIds = new Set(editor.errors.rules[ruleIndex]?.conditionLeaves ?? []);

  return (
    <div className="flex h-full flex-col">
      <ConditionHeader
        name={rule.name}
        editable={!tooDeep}
        onChange={(name) => editor.updateRule(ruleIndex, (r) => setName(r, name))}
        onClose={() => editor.select(null)}
      />

      <div className="min-h-0 flex-1 overflow-auto p-3">
        {tooDeep ? (
          <Alert variant="warning">
            <AlertTitle>Can't edit this condition</AlertTitle>
            <AlertDescription>
              This condition extends beyond what is editable in this UI. Please contact support.
            </AlertDescription>
          </Alert>
        ) : (
          <ConditionGroup
            root={rule.condition}
            scopeType={scopeType}
            incompleteLeafIds={incompleteLeafIds}
            updateRule={(recipe) => editor.updateRule(ruleIndex, recipe)}
          />
        )}
      </div>

      {!tooDeep && (
        <div className="flex gap-2 border-t p-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => editor.updateRule(ruleIndex, (r) => addLeaf(r, scopeType))}
          >
            <SplitIcon />
            Add condition
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => editor.updateRule(ruleIndex, (r) => addGroup(r, scopeType))}
          >
            <FolderPlusIcon />
            Add group
          </Button>
        </div>
      )}
    </div>
  );
}
